/**
 * HQ -> New Joiners, run by Bella (user, 2026-10-27).
 *
 * HR adds a joiner — with the offer letter date, joining date and CTC — and
 * Bella does the rest: the offer letter on its date (straight away if that is
 * today), the pre-joining email 2 days before joining once the signed copy is
 * in, and the welcome email on joining day once the official email ID is
 * filled. The rules live in oh_operations/services/new_joiner_emails.py; this
 * page shows what Bella is doing and offers Preview / Hold / Retry.
 *
 * Because adding a joiner is what sends the offer, the add form is strict and
 * ends on a review step that spells out exactly what Bella will send, to whom
 * and when. The old manual checklist is hidden (its data is kept).
 */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import dayjs from 'dayjs'
import React from 'react'

import {
  createNewJoiner,
  decideNewJoinerRequest,
  deleteNewJoiner,
  fetchIdCardPdf,
  fetchNewJoinerDetails,
  fetchNewJoinerDocument,
  fetchNewJoiners,
  fetchOfferLetterPdf,
  holdNewJoinerEmail,
  previewNewJoinerEmail,
  revealNewJoinerDetail,
  sendNewJoinerEmail,
  updateNewJoiner
} from 'api/newJoiners'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  role: '',
  centre: '',
  personal_email: '',
  phone: '',
  designation: '',
  ctc: '',
  offer_date: '',
  joining_date: '',
  address: '',
  stipend: '',
  internship_months: '',
  reporting_time: '',
  onboarding_contact: '',
  manager_name: '',
  manager_email: '',
  employment_type: ''
}

// '08:30' -> '8:30 AM'
const formatTime = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '')
  if (!m) return hhmm || ''
  const h = Number(m[1])
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`
}

const FORM_KEYS = Object.keys(EMPTY_FORM)

const EMAIL_STATE_STYLE = {
  scheduled: { label: 'Scheduled', bg: '#e8f0fe', fg: '#1a4fa0' },
  due: { label: 'Due', bg: '#fff4e0', fg: '#8a5300' },
  waiting: { label: 'Waiting', bg: '#f1f1f1', fg: '#616161' },
  held: { label: 'On hold', bg: '#f1f1f1', fg: '#616161' },
  sent: { label: 'Sent', bg: '#e6f4ea', fg: '#1e6b34' }
}

// One colour per kind of action so the joiner window reads at a glance
// (user, 2026-10-28): blue = look/change, green = go, amber = pause,
// red = destructive or needs fixing, grey = leave.
const TONES = {
  info: '#1565c0',
  go: '#2e7d32',
  pause: '#b26a00',
  danger: '#c62828',
  neutral: '#5f6368'
}

// Left edge of each email row, by state (failed overrides).
const STATE_EDGE = {
  sent: TONES.go,
  scheduled: TONES.info,
  due: TONES.pause,
  waiting: '#bdbdbd',
  held: '#bdbdbd',
  failed: TONES.danger
}

const ActionButton = ({ tone, filled, disabled, style, ...props }) => {
  const c = TONES[tone] || TONES.neutral
  return (
    <Button
      size="small"
      variant={filled ? 'contained' : 'outlined'}
      disabled={disabled}
      style={{
        ...(filled
          ? { background: c, color: '#fff' }
          : { color: c, borderColor: c }),
        opacity: disabled ? 0.5 : 1,
        textTransform: 'none',
        ...style
      }}
      {...props}
    />
  )
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const errorDetail = (e) => e?.response?.data?.detail || constants.ERROR_MESSAGE

const formatDate = (iso) => (iso ? dayjs(iso).format('D MMM YYYY') : '')

const today = () => dayjs().format('YYYY-MM-DD')

const digitsOnly = (v) => String(v || '').replace(/[^\d]/g, '')

// '420000' -> '₹4,20,000' (Indian grouping), matching the backend's format_inr.
const formatInr = (v) => {
  const d = digitsOnly(v)
  if (!d) return ''
  const tail = d.slice(-3)
  let head = d.slice(0, -3)
  const groups = []
  while (head.length > 2) {
    groups.unshift(head.slice(-2))
    head = head.slice(0, -2)
  }
  if (head) groups.unshift(head)
  return `₹${[...groups, tail].join(',')}`
}

// List header: bigger and bold (user, 2026-10-28).
const HEADER_CELL = { fontSize: 15, fontWeight: 700 }

const STEP_STYLE = {
  pending: { label: 'Pending', bg: '#f1f1f1', fg: '#616161' },
  in_progress: { label: 'In progress', bg: '#fff4e0', fg: '#8a5300' },
  done: { label: 'Done', bg: '#e6f4ea', fg: '#1e6b34' }
}

const STEP_EDGE = {
  pending: '#bdbdbd',
  in_progress: '#b26a00',
  done: '#2e7d32'
}

const Pill = ({ style }) => (
  <span
    style={{
      fontSize: 12,
      padding: '2px 8px',
      borderRadius: 4,
      background: style.bg,
      color: style.fg,
      whiteSpace: 'nowrap'
    }}
  >
    {style.label}
  </span>
)

// Onboarding progress (user, 2026-10-28). A joiner's tasks are the three
// emails, the signed offer letter, and their onboarding steps; they are
// "completed" when every task is done.
const tasksOf = (j) => {
  const emailsDone = j.emails.filter((e) => e.state === 'sent').length
  const stepsDone = j.steps.filter((st) => st.status === 'done').length
  const signed = j.signed_offer ? 1 : 0
  return {
    done: emailsDone + signed + stepsDone,
    total: j.emails.length + 1 + j.steps.length
  }
}

const isCompleted = (j) => {
  const t = tasksOf(j)
  return t.total > 0 && t.done === t.total
}

const emailSent = (j, kind) =>
  (j.emails.find((e) => e.kind === kind) || {}).state === 'sent'

// The journey, in order. A stage is done when its condition holds.
const stagesOf = (j) => [
  { label: 'Offer sent', done: emailSent(j, 'offer_letter') },
  { label: 'Signed', done: Boolean(j.signed_offer) },
  { label: 'Pre-joining sent', done: emailSent(j, 'pre_joining') },
  { label: 'Joined', done: emailSent(j, 'welcome') },
  {
    label: 'Onboarded',
    done: j.steps.length > 0 && j.steps.every((st) => st.status === 'done')
  }
]

// Emails and steps in HR's order (user, 2026-10-29). Anything not listed
// goes at the end.
const JOURNEY_ORDER = [
  'email:offer_letter',
  'signed',
  'email:pre_joining',
  'step:openhouse_email_id',
  'email:welcome',
  'step:slack_access',
  'step:alfred_account',
  'step:teacher_account',
  'step:id_card',
  'email:id_card',
  'step:class_mapping',
  'step:keka_account',
  'step:insurance'
]

const journeyOf = (j) => {
  const items = [
    ...j.emails.map((email) => ({ id: `email:${email.kind}`, email })),
    { id: 'signed' },
    ...j.steps.map((step) => ({ id: `step:${step.key}`, step }))
  ]
  const rank = (id) => {
    const i = JOURNEY_ORDER.indexOf(id)
    return i === -1 ? JOURNEY_ORDER.length : i
  }
  return items.sort((a, b) => rank(a.id) - rank(b.id))
}

const pct = (done, total) => (total ? Math.round((done / total) * 100) : 0)

const Bar = ({ value, color, height = 10 }) => (
  <Box
    style={{
      height,
      borderRadius: height / 2,
      background: '#eceff1',
      overflow: 'hidden'
    }}
  >
    <Box
      style={{
        width: `${value}%`,
        height: '100%',
        background: color,
        transition: 'width .3s'
      }}
    />
  </Box>
)

// Top of the page: completed vs pending across the joiners in view.
const CompletionSummary = ({ joiners }) => {
  const completed = joiners.filter(isCompleted).length
  const pending = joiners.length - completed
  const totals = joiners.reduce(
    (acc, j) => {
      const t = tasksOf(j)
      return { done: acc.done + t.done, total: acc.total + t.total }
    },
    { done: 0, total: 0 }
  )
  const overall = pct(totals.done, totals.total)
  const Count = ({ label, value, color }) => (
    <Paper
      variant="outlined"
      style={{
        padding: '14px 18px',
        borderLeft: `5px solid ${color}`,
        borderRadius: 0,
        height: '100%'
      }}
    >
      <Typography variant="body2" style={{ fontWeight: 700, color }}>
        {label}
      </Typography>
      <Typography style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        joiner{value === 1 ? '' : 's'}
      </Typography>
    </Paper>
  )
  return (
    <Grid container spacing={2} style={{ marginBottom: 16 }}>
      <Grid item xs={6} sm={3}>
        <Count label="Completed" value={completed} color="#2e7d32" />
      </Grid>
      <Grid item xs={6} sm={3}>
        <Count label="Pending" value={pending} color="#b26a00" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Paper
          variant="outlined"
          style={{ padding: '14px 18px', height: '100%' }}
        >
          <Box display="flex" alignItems="baseline" style={{ gap: 8 }} mb={1}>
            <Typography style={{ fontWeight: 700 }}>
              Onboarding completion
            </Typography>
            <Typography style={{ fontSize: 26, fontWeight: 800 }}>
              {overall}%
            </Typography>
          </Box>
          <Bar value={overall} color="#2e7d32" height={12} />
          <Typography
            variant="caption"
            color="textSecondary"
            component="p"
            style={{ marginTop: 6 }}
          >
            {totals.done} tasks done · {totals.total - totals.done} pending
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  )
}

// Inside a joiner's window: their journey and their own task bar.
const JoinerTracker = ({ joiner }) => {
  const stages = stagesOf(joiner)
  const current = stages.findIndex((st) => !st.done)
  const t = tasksOf(joiner)
  const done = pct(t.done, t.total)
  return (
    <Paper
      variant="outlined"
      style={{ padding: '14px 16px', marginBottom: 16 }}
    >
      <Box display="flex" alignItems="flex-start">
        {stages.map((st, i) => {
          const isCurrent = i === current
          let ring = '#bdbdbd'
          if (st.done) ring = '#2e7d32'
          else if (isCurrent) ring = '#b26a00'
          return (
            <React.Fragment key={st.label}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                style={{ width: 92, flexShrink: 0 }}
              >
                <Box
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: `3px solid ${ring}`,
                    background: st.done ? '#2e7d32' : '#fff',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    fontWeight: 800
                  }}
                >
                  {st.done ? '✓' : ''}
                </Box>
                <Typography
                  variant="caption"
                  style={{
                    marginTop: 6,
                    textAlign: 'center',
                    fontWeight: isCurrent || st.done ? 700 : 400,
                    color: isCurrent ? '#8a5300' : undefined
                  }}
                >
                  {st.label}
                </Typography>
              </Box>
              {i < stages.length - 1 && (
                <Box
                  flex={1}
                  style={{
                    height: 3,
                    marginTop: 13,
                    background: st.done ? '#2e7d32' : '#e0e0e0'
                  }}
                />
              )}
            </React.Fragment>
          )
        })}
      </Box>
      <Box display="flex" alignItems="center" style={{ gap: 12 }} mt={2}>
        <Box flex={1}>
          <Bar value={done} color={done === 100 ? '#2e7d32' : '#1565c0'} />
        </Box>
        <Typography variant="body2" style={{ fontWeight: 700 }}>
          {t.done} of {t.total} tasks done
        </Typography>
      </Box>
    </Paper>
  )
}

// Client-side checks mirror the API's, so mistakes show before the review step.
// `role` is the role's flags from the API ({hq, intern, has_letter}).
const validate = (form, isAdd, role) => {
  const missing = []
  if (!form.first_name.trim()) missing.push('first name')
  if (!form.last_name.trim()) missing.push('last name')
  if (!form.role) missing.push('role')
  if (!role.hq && !form.centre) missing.push('centre')
  if (!form.personal_email.trim()) missing.push('personal email')
  if (!form.phone.trim()) missing.push('phone number')
  if (!form.manager_name.trim()) missing.push("reporting manager's name")
  if (!form.manager_email.trim()) missing.push("reporting manager's email")
  if (!form.designation.trim()) missing.push('designation')
  if (!form.employment_type) missing.push('employment type')
  if (!role.intern && !form.address.trim()) missing.push('address')
  if (role.intern) {
    if (!digitsOnly(form.stipend)) missing.push('monthly stipend')
    if (!form.internship_months) missing.push('internship duration')
  } else if (!digitsOnly(form.ctc)) missing.push('CTC')
  if (!form.offer_date) missing.push('offer letter date')
  if (!form.joining_date) missing.push('joining date')
  if (!form.reporting_time) missing.push('reporting time')
  if (!form.onboarding_contact.trim()) missing.push('onboarding contact')
  if (missing.length) return `Fill in the ${missing.join(', ')}.`
  const phone = form.phone.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '')
  if (phone.length !== 10)
    return 'The phone number must be a 10-digit mobile number (it is their Alfred login).'
  if (!/@openhouse\.study$/i.test(form.manager_email.trim()))
    return "The reporting manager's email must be their @openhouse.study address."
  if (!EMAIL_RE.test(form.personal_email.trim()))
    return "The personal email doesn't look like an email address."
  if (isAdd && form.offer_date < today())
    return 'The offer letter date is in the past. Use today or a later date.'
  if (form.joining_date < form.offer_date)
    return 'The joining date is before the offer letter date.'
  return ''
}

const ReviewRow = ({ label, value }) => (
  <Box display="flex" py={0.5} style={{ gap: 12 }}>
    <Typography
      variant="body2"
      color="textSecondary"
      style={{ width: 150, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" style={{ fontWeight: 600 }}>
      {value || '—'}
    </Typography>
  </Box>
)

const JoinerDialog = ({
  open,
  initial,
  roles,
  centres,
  employmentTypes,
  testMode,
  testTo,
  onClose,
  onSave
}) => {
  const [form, setForm] = React.useState(EMPTY_FORM)
  const [reviewing, setReviewing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const isAdd = !initial

  // Fill the form when it OPENS only. The page refreshes the joiner's data in
  // the background, which hands this dialog a new `initial`; resetting on that
  // wiped what HR was typing (user, 2026-10-30).
  const wasOpen = React.useRef(false)
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setForm(initial || { ...EMPTY_FORM, offer_date: today() })
      setReviewing(false)
      setError('')
    }
    wasOpen.current = open
  }, [open, initial])

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value })
    setError('')
  }

  const setRole = (e) => {
    const next = roles.find((r) => r.value === e.target.value) || {}
    const prev = roles.find((r) => r.value === form.role) || {}
    const untouched =
      !form.reporting_time || form.reporting_time === prev.reporting_time
    setForm({
      ...form,
      role: e.target.value,
      // The role's default (intern / part time for Afterschool / full time),
      // unless HR picked Contract by hand.
      employment_type:
        form.employment_type === 'contract' && !next.intern
          ? 'contract'
          : next.employment_type || 'full_time',
      reporting_time: untouched
        ? next.reporting_time || ''
        : form.reporting_time
    })
    setError('')
  }

  const role = roles.find((r) => r.value === form.role) || {}

  const save = async () => {
    setSaving(true)
    try {
      await onSave({
        ...form,
        ctc: role.intern ? '' : digitsOnly(form.ctc),
        stipend: role.intern ? digitsOnly(form.stipend) : '',
        internship_months: role.intern ? form.internship_months : '',
        centre: form.centre || ''
      })
    } catch (e) {
      setError(errorDetail(e))
      setReviewing(false)
    } finally {
      setSaving(false)
    }
  }

  const next = () => {
    const problem = validate(form, isAdd, role)
    if (problem) {
      setError(problem)
      return
    }
    if (isAdd) setReviewing(true)
    else save()
  }

  const roleLabel = role.label
  const centreName =
    (centres.find((c) => c.uuid === form.centre) || {}).name ||
    (role.hq ? 'Headquarters' : '')
  const monthlyCtc = digitsOnly(form.ctc)
    ? Number(digitsOnly(form.ctc)) / 12
    : 0
  // Any amount is allowed (user, 2026-10-28); the monthly figure is
  // approximate when the yearly one doesn't divide by 12.
  const ctcText = form.ctc
    ? `${formatInr(form.ctc)} a year (${
        Number.isInteger(monthlyCtc) ? '' : 'about '
      }${formatInr(String(Math.round(monthlyCtc)))} a month)`
    : ''
  const offerToday = form.offer_date === today()
  const sendTo = testMode ? testTo : form.personal_email.trim()

  let title = 'Edit joiner'
  if (reviewing) title = 'Check before Bella sends'
  else if (isAdd) title = 'Add joiner'

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {reviewing ? (
          <>
            <ReviewRow
              label="Name"
              value={`${form.first_name.trim()} ${form.last_name.trim()}`}
            />
            <ReviewRow label="Role" value={roleLabel} />
            <ReviewRow label="Designation" value={form.designation.trim()} />
            <ReviewRow
              label="Employment type"
              value={
                (
                  employmentTypes.find(
                    (t) => t.value === form.employment_type
                  ) || {}
                ).label
              }
            />
            <ReviewRow label="Centre" value={centreName} />
            {!role.intern && (
              <ReviewRow label="Address" value={form.address.trim()} />
            )}
            {role.intern ? (
              <>
                <ReviewRow
                  label="Monthly stipend"
                  value={formatInr(form.stipend)}
                />
                <ReviewRow
                  label="Duration"
                  value={
                    form.internship_months
                      ? `${form.internship_months} month${
                          form.internship_months === '1' ? '' : 's'
                        }`
                      : ''
                  }
                />
              </>
            ) : (
              <ReviewRow label="CTC" value={ctcText} />
            )}
            <ReviewRow
              label="Offer letter date"
              value={formatDate(form.offer_date)}
            />
            <ReviewRow
              label="Joining date"
              value={formatDate(form.joining_date)}
            />
            <ReviewRow
              label="Personal email"
              value={form.personal_email.trim()}
            />
            <ReviewRow
              label="Reporting manager"
              value={`${form.manager_name} (${form.manager_email}), cc'd on the pre-joining email`}
            />
            <ReviewRow
              label="Day one"
              value={`Report at ${formatTime(
                form.reporting_time
              )}, ask for ${form.onboarding_contact.trim()}`}
            />
            <Typography
              variant="body2"
              style={{
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: 4,
                background: '#e8f0fe',
                color: '#1a4fa0'
              }}
            >
              {offerToday
                ? `Bella will email the offer letter to ${sendTo} as soon as you confirm.`
                : `Bella will email the offer letter to ${sendTo} on ${formatDate(
                    form.offer_date
                  )} at 9 AM.`}{' '}
              The pre-joining email follows 2 days before joining, once the
              signed copy is back, and the welcome email on{' '}
              {formatDate(form.joining_date)}, once the official email ID is
              filled in.
              {testMode &&
                ' Test mode is on, so every email goes to you, not the joiner.'}
            </Typography>
            {!role.has_letter && (
              <Typography
                variant="body2"
                style={{
                  marginTop: 8,
                  padding: '10px 12px',
                  borderRadius: 4,
                  background: '#fff4e0',
                  color: '#8a5300'
                }}
              >
                There is no offer letter for {roleLabel} yet, so Bella will hold
                the offer until it is added.
              </Typography>
            )}
          </>
        ) : (
          <>
            {/* First and last, as Alfred asks for them (user, 2026-10-29). */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First name"
                  required
                  value={form.first_name}
                  onChange={set('first_name')}
                  fullWidth
                  margin="dense"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last name"
                  required
                  value={form.last_name}
                  onChange={set('last_name')}
                  fullWidth
                  margin="dense"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required>
                  <InputLabel>Role</InputLabel>
                  <Select value={form.role} onChange={setRole}>
                    {roles.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" required={!role.hq}>
                  <InputLabel>Centre</InputLabel>
                  <Select
                    value={form.centre || ''}
                    onChange={set('centre')}
                    displayEmpty={Boolean(role.hq)}
                  >
                    {role.hq && <MenuItem value="">Headquarters</MenuItem>}
                    {centres.map((c) => (
                      <MenuItem key={c.uuid} value={c.uuid}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Designation"
              required
              value={form.designation}
              onChange={set('designation')}
              fullWidth
              margin="dense"
              helperText="Exactly as it should appear on the offer letter."
            />
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Employment type</InputLabel>
              <Select
                value={form.employment_type || ''}
                onChange={set('employment_type')}
              >
                {employmentTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {role.intern ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monthly stipend (₹)"
                    required
                    value={form.stipend}
                    onChange={(e) => {
                      setForm({ ...form, stipend: digitsOnly(e.target.value) })
                      setError('')
                    }}
                    fullWidth
                    margin="dense"
                    inputProps={{ inputMode: 'numeric' }}
                    placeholder="15000"
                    helperText={
                      form.stipend
                        ? `On the letter: Rs. ${formatInr(form.stipend).slice(
                            1
                          )}/- per month`
                        : 'Digits only, rupees per month.'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense" required>
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={String(form.internship_months || '')}
                      onChange={set('internship_months')}
                    >
                      {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(
                        (m) => (
                          <MenuItem key={m} value={m}>
                            {m} month{m === '1' ? '' : 's'}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            ) : (
              <TextField
                label="Annual CTC (₹)"
                required
                value={form.ctc}
                onChange={(e) => {
                  setForm({ ...form, ctc: digitsOnly(e.target.value) })
                  setError('')
                }}
                fullWidth
                margin="dense"
                inputProps={{ inputMode: 'numeric' }}
                placeholder="480000"
                helperText={
                  form.ctc
                    ? `On the letter: ${ctcText}. Bella builds the salary table from this.`
                    : 'Digits only, the yearly amount in rupees.'
                }
              />
            )}
            {!role.intern && (
              <TextField
                label="Address"
                required
                value={form.address}
                onChange={set('address')}
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                helperText="Printed under the joiner's name on the letter."
              />
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Offer letter date"
                  required
                  type="date"
                  value={form.offer_date}
                  onChange={set('offer_date')}
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  inputProps={isAdd ? { min: today() } : {}}
                  helperText="Bella sends the offer on this day."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Joining date"
                  required
                  type="date"
                  value={form.joining_date}
                  onChange={set('joining_date')}
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: form.offer_date || undefined }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reporting time on day one"
                  required
                  type="time"
                  value={form.reporting_time}
                  onChange={set('reporting_time')}
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  helperText="Pre-filled from the role's working hours."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Ask for (onboarding contact)"
                  required
                  value={form.onboarding_contact}
                  onChange={set('onboarding_contact')}
                  fullWidth
                  margin="dense"
                  placeholder="Priya, Centre Director"
                  helperText="Who welcomes them on day one."
                />
              </Grid>
            </Grid>
            {/* Reporting manager (HR, 2026-10-30), typed by whoever adds the
                joiner: CC'd on the pre-joining email, needed for Keka. */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reporting manager's name"
                  required
                  value={form.manager_name}
                  onChange={set('manager_name')}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reporting manager's email"
                  required
                  value={form.manager_email}
                  onChange={set('manager_email')}
                  fullWidth
                  margin="dense"
                  placeholder="name@openhouse.study"
                  helperText="CC'd on the pre-joining email."
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Personal email"
                  required
                  value={form.personal_email}
                  onChange={set('personal_email')}
                  fullWidth
                  margin="dense"
                  helperText="Bella's emails go here."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  required
                  value={form.phone}
                  onChange={set('phone')}
                  fullWidth
                  margin="dense"
                />
              </Grid>
            </Grid>
          </>
        )}
        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {reviewing ? (
          <>
            <Button onClick={() => setReviewing(false)} disabled={saving}>
              Back to edit
            </Button>
            <Button
              onClick={save}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving ? 'Adding…' : 'Confirm and add'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={next}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {isAdd ? 'Review' : saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

const emailDetail = (email) => {
  const last = email.last || {}
  if (email.state === 'sent') {
    const meant = last.test_redirected
      ? ` (test — meant for ${last.intended_to})`
      : ''
    return `Sent to ${last.to}${meant} on ${formatDate(last.at)}${
      last.by ? ` by ${last.by}` : ''
    }`
  }
  if (email.state === 'held') return "On hold — Bella won't send it."
  if (email.state === 'waiting') return email.blocker
  if (email.state === 'scheduled')
    return `Bella sends it on ${formatDate(email.due_date)} at 9 AM.`
  return 'Bella sends it at the next 9 AM run.'
}

const EmailRow = ({ email, busy, onPreview, onSend, onHold }) => {
  const last = email.last || {}
  const failed = last.status === 'failed' && email.state !== 'sent'
  let sendLabel = 'Resend'
  if (busy) sendLabel = 'Sending…'
  else if (failed) sendLabel = 'Retry now'
  const edge = failed ? STATE_EDGE.failed : STATE_EDGE[email.state] || '#bdbdbd'
  return (
    <Paper
      variant="outlined"
      style={{
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        borderLeft: `4px solid ${edge}`,
        borderRadius: 0
      }}
    >
      <Box flex={1} minWidth={220}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {email.label}
          </Typography>
          <Pill
            style={EMAIL_STATE_STYLE[email.state] || EMAIL_STATE_STYLE.due}
          />
        </Box>
        <Typography variant="caption" color="textSecondary">
          {emailDetail(email)}
        </Typography>
        {failed && (
          <Typography variant="caption" color="error" component="p">
            Last attempt failed: {last.error}
          </Typography>
        )}
      </Box>
      <ActionButton tone="info" onClick={onPreview} disabled={busy}>
        Preview
      </ActionButton>
      {email.state !== 'sent' && (
        <ActionButton
          tone={email.state === 'held' ? 'go' : 'pause'}
          onClick={onHold}
          disabled={busy}
        >
          {email.state === 'held' ? 'Resume' : 'Hold'}
        </ActionButton>
      )}
      {(failed || email.state === 'sent') && (
        <ActionButton
          tone={failed ? 'danger' : 'info'}
          filled={failed}
          onClick={onSend}
          disabled={busy}
        >
          {sendLabel}
        </ActionButton>
      )}
    </Paper>
  )
}

// One onboarding step, laid out like Bella's email rows (user, 2026-10-28):
// coloured edge, label + status, a detail line, and buttons for the moves.
// The official email ID step is done by itself once the email is filled in,
// so it has no buttons then.
const stepDetail = (step) => {
  if (step.auto) return `Done automatically: ${step.detail}`
  if (step.detail && step.status !== 'done') return step.detail
  const who = step.by ? ` by ${step.by}` : ''
  if (step.status === 'done') return `Done on ${formatDate(step.at)}${who}`
  if (step.status === 'in_progress')
    return `In progress since ${formatDate(step.at)}${who}`
  return 'Not started yet.'
}

const StepRow = ({ step, busy, onSet, extra }) => (
  <Paper
    variant="outlined"
    style={{
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      borderLeft: `4px solid ${STEP_EDGE[step.status] || STEP_EDGE.pending}`,
      borderRadius: 0
    }}
  >
    <Box flex={1} minWidth={220}>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {step.label}
        </Typography>
        <Pill style={STEP_STYLE[step.status] || STEP_STYLE.pending} />
      </Box>
      <Typography variant="caption" color="textSecondary">
        {stepDetail(step)}
      </Typography>
    </Box>
    {extra}
    {!step.auto && step.status === 'pending' && (
      <ActionButton
        tone="pause"
        onClick={() => onSet('in_progress')}
        disabled={busy}
      >
        Start
      </ActionButton>
    )}
    {!step.auto && step.status !== 'done' && (
      <ActionButton tone="go" onClick={() => onSet('done')} disabled={busy}>
        Mark done
      </ActionButton>
    )}
    {!step.auto && step.status === 'in_progress' && (
      <ActionButton
        tone="neutral"
        onClick={() => onSet('pending')}
        disabled={busy}
      >
        Back to pending
      </ActionButton>
    )}
    {!step.auto && step.status === 'done' && (
      <ActionButton
        tone="neutral"
        onClick={() => onSet('pending')}
        disabled={busy}
      >
        Reopen
      </ActionButton>
    )}
  </Paper>
)

// The joining-details form answers (Keka + ID card). Aadhaar, PAN, UAN and
// bank numbers come masked; Reveal fetches one in full and the server logs it
// in the joiner's activity. Documents open through 5-minute links (logged).
const DetailsDialog = ({ joiner, onClose }) => {
  const [data, setData] = React.useState(null)
  const [error, setError] = React.useState('')
  const [revealed, setRevealed] = React.useState({})

  React.useEffect(() => {
    if (!joiner) return
    setData(null)
    setError('')
    setRevealed({})
    fetchNewJoinerDetails(joiner.uuid)
      .then(setData)
      .catch((e) => setError(errorDetail(e)))
  }, [joiner])

  const reveal = async (key) => {
    try {
      const { value } = await revealNewJoinerDetail(joiner.uuid, key)
      setRevealed((r) => ({ ...r, [key]: value }))
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    }
  }

  const openDocument = async (key) => {
    try {
      const { url } = await fetchNewJoinerDocument(joiner.uuid, key)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    }
  }

  const rows = data?.rows || []
  return (
    <Dialog open={Boolean(joiner)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Joining details · {joiner?.name}</DialogTitle>
      <DialogContent dividers>
        {error && <Typography color="error">{error}</Typography>}
        {!data && !error && <CircularProgress size={24} />}
        {data && !data.submitted && (
          <Typography variant="body2">
            Not filled yet. The form link goes out with the welcome email.
          </Typography>
        )}
        {data?.submitted && (
          <>
            <Typography variant="caption" color="textSecondary">
              Sent {dayjs(data.at).format('D MMM YYYY, h:mm A')}. Revealing a
              number or opening a document is recorded in Activity.
            </Typography>
            {rows.map((row, i) => (
              <React.Fragment key={row.key}>
                {(i === 0 || rows[i - 1].section !== row.section) && (
                  <Typography
                    variant="subtitle2"
                    style={{ marginTop: 14, fontWeight: 700 }}
                  >
                    {row.section}
                  </Typography>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  py={0.5}
                  style={{ gap: 12, borderBottom: '1px solid #f1f1f1' }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ width: 200, flexShrink: 0 }}
                  >
                    {row.label.split('. If')[0]}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ flex: 1, whiteSpace: 'pre-wrap' }}
                  >
                    {row.document ? '' : revealed[row.key] || row.value}
                  </Typography>
                  {row.masked && !revealed[row.key] && (
                    <ActionButton tone="info" onClick={() => reveal(row.key)}>
                      Reveal
                    </ActionButton>
                  )}
                  {row.document && (
                    <ActionButton
                      tone="info"
                      onClick={() => openDocument(row.key)}
                    >
                      Open
                    </ActionButton>
                  )}
                </Box>
              </React.Fragment>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <ActionButton tone="neutral" onClick={onClose}>
          Close
        </ActionButton>
      </DialogActions>
    </Dialog>
  )
}

const PreviewDialog = ({ preview, onClose, onOpenPdf }) => (
  <Dialog open={Boolean(preview)} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>{preview?.subject}</DialogTitle>
    <DialogContent>
      {preview?.error ? (
        <Typography color="error">{preview.error}</Typography>
      ) : (
        <iframe
          title="Email preview"
          sandbox=""
          srcDoc={preview?.html || ''}
          style={{
            width: '100%',
            height: 420,
            border: '1px solid #e0e0e0',
            borderRadius: 4
          }}
        />
      )}
    </DialogContent>
    <DialogActions>
      {preview?.has_letter && (
        <Button onClick={onOpenPdf}>View offer letter PDF</Button>
      )}
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
)

// The signed offer letter as one of Bella's steps: done when the joiner
// uploads it from the link in the offer email (that's what unlocks the
// pre-joining email). The joiner signs online at the link (2026-10-28).
// No manual moves — View / Copy signing link only.
const SignedOfferRow = ({ joiner }) => {
  const signed = joiner.signed_offer
  let state = 'pending'
  let detail = 'The signing link goes out with the offer letter.'
  if (signed) {
    state = 'done'
    detail = `Signed online on ${formatDate(signed.at)}`
  } else if (joiner.upload_link) {
    state = 'in_progress'
    detail = 'Waiting for the joiner to sign it online.'
  }
  const labels = { pending: 'Pending', in_progress: 'Waiting', done: 'Done' }
  return (
    <Paper
      variant="outlined"
      style={{
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        borderLeft: `4px solid ${STEP_EDGE[state]}`,
        borderRadius: 0
      }}
    >
      <Box flex={1} minWidth={220}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Signed offer letter
          </Typography>
          <Pill style={{ ...STEP_STYLE[state], label: labels[state] }} />
        </Box>
        <Typography variant="caption" color="textSecondary">
          {detail}
        </Typography>
      </Box>
      {signed && signed.url && (
        <ActionButton
          tone="info"
          component="a"
          href={signed.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </ActionButton>
      )}
      {!signed && joiner.upload_link && (
        <ActionButton
          tone="info"
          onClick={() => {
            navigator.clipboard.writeText(joiner.upload_link)
            showToast('Signing link copied', constants.SUCCESS)
          }}
        >
          Copy signing link
        </ActionButton>
      )}
    </Paper>
  )
}

// What the joiner asked for, or Bella escalated (bella_requests.py, user
// 2026-10-28). Bella never changes the offer herself: HR decides here.
const DECISIONS = {
  approve: { label: 'Approve new date', tone: 'go', filled: true },
  decline: { label: 'Keep current date', tone: 'danger' },
  resolve: { label: 'Mark resolved', tone: 'go', filled: true },
  acknowledge: { label: 'Got it', tone: 'neutral' },
  keep_waiting: { label: 'Keep waiting', tone: 'pause' },
  withdraw: { label: 'Withdraw offer', tone: 'danger' }
}

const openRequestsOf = (j) =>
  (j.requests || []).filter((r) => r.state === 'open')

const isActive = (j) => !j.status || j.status === 'active'

const RequestCard = ({ joiner, request, busy, onDecide, onOpen }) => (
  <Paper
    variant="outlined"
    style={{
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      borderLeft: `4px solid ${TONES.pause}`,
      borderRadius: 0,
      background: '#fffaf0'
    }}
  >
    <Box flex={1} minWidth={240}>
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        {onOpen ? (
          <Link component="button" variant="body2" onClick={onOpen}>
            {joiner.name}
          </Link>
        ) : null}
        {onOpen ? ' · ' : ''}
        {request.label}
      </Typography>
      <Typography variant="body2">{request.summary}</Typography>
      <Typography variant="caption" color="textSecondary">
        {dayjs(request.at).format('D MMM, h:mm A')}
      </Typography>
    </Box>
    {request.decisions.map((d) => (
      <ActionButton
        key={d}
        tone={DECISIONS[d]?.tone}
        filled={DECISIONS[d]?.filled}
        disabled={busy}
        onClick={() => onDecide(joiner, request, d)}
      >
        {DECISIONS[d]?.label || d}
      </ActionButton>
    ))}
  </Paper>
)

const NewJoiners = () => {
  const [data, setData] = React.useState(null)
  const [loadError, setLoadError] = React.useState('')
  const [roleFilter, setRoleFilter] = React.useState('')
  const [centreFilter, setCentreFilter] = React.useState('')
  const [selected, setSelected] = React.useState(null)
  const [dialog, setDialog] = React.useState(null) // null | 'add' | 'edit'
  const [busyEmail, setBusyEmail] = React.useState('')
  const [preview, setPreview] = React.useState(null)

  const load = React.useCallback(async () => {
    try {
      setData(await fetchNewJoiners())
      setLoadError('')
    } catch (e) {
      setLoadError(errorDetail(e))
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  // Joiners act outside Alfred (signing their offer on the public page) and
  // Bella acts on her own schedule, so keep the page current (user,
  // 2026-10-28: a signed offer didn't show until a manual refresh). Refresh
  // quietly every minute while the tab is visible, and on return to the tab.
  // Paused while the add/edit form is open, so nothing changes under it.
  const formOpen = React.useRef(false)
  formOpen.current = Boolean(dialog)
  React.useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible' && !formOpen.current) load()
    }
    const timer = setInterval(refreshIfVisible, 60000)
    document.addEventListener('visibilitychange', refreshIfVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshIfVisible)
    }
  }, [load])

  const joiners = React.useMemo(
    () =>
      (data?.joiners || []).filter(
        (j) =>
          (!roleFilter || j.role === roleFilter) &&
          (!centreFilter || j.centre?.uuid === centreFilter)
      ),
    [data, roleFilter, centreFilter]
  )

  // The panel opens only when a name is clicked (user, 2026-10-08) — no
  // default selection. Filtering the joiner out of view closes it.
  const current = joiners.find((j) => j.uuid === selected) || null

  // Memoized so the dialog's reset-on-open effect doesn't fire on every render
  // and wipe what's being typed.
  const editing = dialog === 'edit' ? current : null
  const dialogInitial = React.useMemo(
    () =>
      editing
        ? FORM_KEYS.reduce(
            (acc, key) => ({
              ...acc,
              [key]:
                key === 'centre'
                  ? editing.centre?.uuid || ''
                  : String(editing[key] ?? '')
            }),
            {}
          )
        : null,
    [editing]
  )

  const replaceJoiner = (row) =>
    setData((d) => ({
      ...d,
      joiners: d.joiners.map((j) => (j.uuid === row.uuid ? row : j))
    }))

  const testMode = Boolean(data?.email_settings?.test_mode)
  const testTo = (data?.email_settings?.test_recipients || [])[0] || ''

  const sendEmail = async (email) => {
    const to = testMode ? testTo : current.personal_email
    const again = email.state === 'sent' ? ' again' : ''
    const what = testMode
      ? `Send a TEST of the ${email.label.toLowerCase()}${again} to ${to}?`
      : `Send the ${email.label.toLowerCase()}${again} to ${to}?`
    // eslint-disable-next-line no-alert
    if (!window.confirm(what)) return
    setBusyEmail(email.kind)
    try {
      replaceJoiner(await sendNewJoinerEmail(current.uuid, email.kind))
      showToast(`${email.label} sent`, constants.SUCCESS)
    } catch (e) {
      const row = e?.response?.data?.joiner
      if (row) replaceJoiner(row)
      showToast(errorDetail(e), constants.ERROR)
    } finally {
      setBusyEmail('')
    }
  }

  const [busyStep, setBusyStep] = React.useState('')

  const setStep = async (step, status) => {
    setBusyStep(step.key)
    try {
      replaceJoiner(
        await updateNewJoiner(current.uuid, { step: step.key, status })
      )
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    } finally {
      setBusyStep('')
    }
  }

  const toggleHold = async (email) => {
    setBusyEmail(email.kind)
    try {
      replaceJoiner(
        await holdNewJoinerEmail(
          current.uuid,
          email.kind,
          email.state !== 'held'
        )
      )
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    } finally {
      setBusyEmail('')
    }
  }

  const openPreview = async (email) => {
    try {
      setPreview({
        ...(await previewNewJoinerEmail(current.uuid, email.kind)),
        uuid: current.uuid
      })
    } catch (e) {
      setPreview({ subject: email.label, error: errorDetail(e) })
    }
  }

  const openOfferPdf = async () => {
    try {
      const blob = await fetchOfferLetterPdf(preview.uuid)
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    }
  }

  const save = async (form) => {
    const body = {
      ...form,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim()
    }
    if (dialog === 'edit') {
      replaceJoiner(await updateNewJoiner(current.uuid, body))
      showToast('Joiner updated', constants.SUCCESS)
    } else {
      const row = await createNewJoiner(body)
      setData((d) => ({ ...d, joiners: [row, ...d.joiners] }))
      const offer = row.emails.find((e) => e.kind === 'offer_letter') || {}
      if (offer.state === 'sent') {
        showToast(
          'Joiner added. Bella sent the offer letter.',
          constants.SUCCESS
        )
      } else if (offer.last?.status === 'failed') {
        showToast(
          `Joiner added, but the offer letter failed: ${offer.last.error}`,
          constants.ERROR
        )
      } else {
        showToast(
          'Joiner added. Bella has scheduled the offer letter.',
          constants.SUCCESS
        )
      }
    }
    setDialog(null)
  }

  const [busyRequest, setBusyRequest] = React.useState('')
  const [detailsFor, setDetailsFor] = React.useState(null)

  const openIdCard = async () => {
    try {
      const blob = await fetchIdCardPdf(current.uuid)
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    }
  }

  const decide = async (joiner, request, decision) => {
    let note = ''
    if (decision === 'decline' && request.type === 'date_change') {
      // eslint-disable-next-line no-alert
      const answer = window.prompt(
        `Bella will tell ${joiner.name} the joining date stays ${formatDate(
          joiner.joining_date
        )}. Add a short note for them (optional):`,
        ''
      )
      if (answer === null) return
      note = answer
    } else {
      const question = {
        approve: `Move ${joiner.name}'s joining date to ${formatDate(
          request.details?.new_date
        )}? Bella will email them the updated offer letter to sign.`,
        withdraw: `Withdraw ${joiner.name}'s offer? Bella stops all their emails.`
      }[decision]
      // eslint-disable-next-line no-alert
      if (question && !window.confirm(question)) return
    }
    setBusyRequest(request.id)
    try {
      replaceJoiner(
        await decideNewJoinerRequest(joiner.uuid, request.id, decision, note)
      )
      showToast('Done. Bella has taken it from here.', constants.SUCCESS)
    } catch (e) {
      const row = e?.response?.data?.joiner
      if (row) replaceJoiner(row)
      showToast(errorDetail(e), constants.ERROR)
    } finally {
      setBusyRequest('')
    }
  }

  // An in-page warning, not window.confirm: browsers can silently suppress
  // those, and a joiner was removed by mistake with no warning (user,
  // 2026-10-29).
  const [confirmRemove, setConfirmRemove] = React.useState(null)
  const [removing, setRemoving] = React.useState(false)

  const removeCurrent = async () => {
    const joiner = confirmRemove
    setRemoving(true)
    try {
      await deleteNewJoiner(joiner.uuid)
      setData((d) => ({
        ...d,
        joiners: d.joiners.filter((j) => j.uuid !== joiner.uuid)
      }))
      setConfirmRemove(null)
      showToast('Joiner removed', constants.SUCCESS)
    } catch (e) {
      showToast(errorDetail(e), constants.ERROR)
    } finally {
      setRemoving(false)
    }
  }

  if (loadError) {
    return (
      <Box p={3}>
        <Typography color="error">{loadError}</Typography>
      </Box>
    )
  }
  if (!data) {
    return (
      <Box p={6} textAlign="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        mb={2}
        style={{ gap: 12 }}
      >
        <Box style={{ flex: 1 }}>
          <Typography variant="h5">New Joiners</Typography>
          <Typography variant="body2" color="textSecondary">
            Bella sends the offer, pre-joining and welcome emails by herself.
            {testMode && ` Test mode: every email goes to ${testTo}.`}
          </Typography>
        </Box>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <MenuItem value="">All roles</MenuItem>
            {data.roles.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centreFilter}
            onChange={(e) => setCentreFilter(e.target.value)}
          >
            <MenuItem value="">All centres</MenuItem>
            {data.centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          color="primary"
          variant="contained"
          onClick={() => setDialog('add')}
        >
          Add joiner
        </Button>
      </Box>

      <CompletionSummary joiners={joiners.filter(isActive)} />

      {joiners.some((j) => openRequestsOf(j).length) && (
        <Box mb={2}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 700, color: TONES.pause }}
            gutterBottom
          >
            Needs your decision
          </Typography>
          <Box display="grid" style={{ gap: 8 }}>
            {joiners.flatMap((j) =>
              openRequestsOf(j).map((r) => (
                <RequestCard
                  key={r.id}
                  joiner={j}
                  request={r}
                  busy={busyRequest === r.id}
                  onDecide={decide}
                  onOpen={() => setSelected(j.uuid)}
                />
              ))
            )}
          </Box>
        </Box>
      )}

      <Paper variant="outlined" style={{ marginBottom: 16, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Role', 'Centre', 'Joining date'].map((h) => (
                <TableCell key={h} style={HEADER_CELL}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {joiners.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No joiners here yet. Use Add joiner to start one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {joiners.map((j) => (
              <TableRow key={j.uuid} selected={current?.uuid === j.uuid}>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    style={{ fontWeight: 600, textAlign: 'left' }}
                    onClick={() => {
                      setSelected(j.uuid)
                      load() // open with the joiner's latest status
                    }}
                  >
                    {j.name}
                  </Link>
                  {!isActive(j) && (
                    <span style={{ marginLeft: 8 }}>
                      <Pill
                        style={{
                          label: j.status_label,
                          bg: '#fdecea',
                          fg: TONES.danger
                        }}
                      />
                    </span>
                  )}
                  {isActive(j) && openRequestsOf(j).length > 0 && (
                    <span style={{ marginLeft: 8 }}>
                      <Pill
                        style={{
                          label: 'Needs your decision',
                          bg: '#fff4e0',
                          fg: '#8a5300'
                        }}
                      />
                    </span>
                  )}
                </TableCell>
                <TableCell>{j.role_label}</TableCell>
                <TableCell>{j.centre_label}</TableCell>
                <TableCell>{formatDate(j.joining_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={Boolean(current)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        {current && (
          <>
            <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                style={{ gap: 8 }}
              >
                <Box flex={1}>
                  <Typography variant="h6">{current.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {current.designation || current.role_label} ·{' '}
                    {current.centre_label} ·{' '}
                    {current.role === 'intern'
                      ? `${
                          formatInr(current.stipend) || 'stipend not set'
                        } a month`
                      : current.ctc_display || 'CTC not set'}{' '}
                    · offer {formatDate(current.offer_date) || 'date not set'} ·
                    joining date {formatDate(current.joining_date)}
                  </Typography>
                </Box>
                <ActionButton tone="info" onClick={() => setDialog('edit')}>
                  Edit
                </ActionButton>
                <ActionButton
                  tone="danger"
                  onClick={() => setConfirmRemove(current)}
                >
                  Remove
                </ActionButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {!isActive(current) && (
                <Typography
                  variant="body2"
                  style={{
                    background: '#fdecea',
                    color: TONES.danger,
                    padding: '8px 12px',
                    borderRadius: 4,
                    marginBottom: 12,
                    fontWeight: 600
                  }}
                >
                  {current.status_label}. Bella has stopped all emails and steps
                  for {current.name}.
                </Typography>
              )}
              {openRequestsOf(current).length > 0 && (
                <Box display="grid" style={{ gap: 8 }} mb={2}>
                  {openRequestsOf(current).map((r) => (
                    <RequestCard
                      key={r.id}
                      joiner={current}
                      request={r}
                      busy={busyRequest === r.id}
                      onDecide={decide}
                    />
                  ))}
                </Box>
              )}
              <JoinerTracker joiner={current} />
              {testMode && (
                <Typography
                  variant="body2"
                  style={{
                    background: '#fff4e0',
                    color: '#8a5300',
                    padding: '6px 10px',
                    borderRadius: 4,
                    marginBottom: 12
                  }}
                >
                  Test mode: every email goes to {testTo}, not to the joiner.
                </Typography>
              )}
              <Typography variant="subtitle2" gutterBottom>
                Bella&apos;s onboarding steps · {tasksOf(current).done} of{' '}
                {tasksOf(current).total} done
              </Typography>
              <Box display="grid" style={{ gap: 8 }} mb={2}>
                {journeyOf(current).map(({ id, email, step }) => {
                  if (id === 'signed')
                    return <SignedOfferRow key={id} joiner={current} />
                  if (email)
                    return (
                      <EmailRow
                        key={id}
                        email={email}
                        busy={busyEmail === email.kind}
                        onPreview={() => openPreview(email)}
                        onSend={() => sendEmail(email)}
                        onHold={() => toggleHold(email)}
                      />
                    )
                  return (
                    <StepRow
                      key={step.key}
                      step={step}
                      busy={busyStep === step.key}
                      onSet={(status) => setStep(step, status)}
                      extra={
                        step.issue_link ? (
                          // Bella's Batch & Schedule issue for class mapping.
                          <ActionButton
                            tone="info"
                            component="a"
                            href={step.issue_link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open in Slack
                          </ActionButton>
                        ) : (
                          current.details_submitted_at &&
                          (step.key === 'keka_account' ? (
                            <ActionButton
                              tone="info"
                              onClick={() => setDetailsFor(current)}
                            >
                              View details
                            </ActionButton>
                          ) : step.key === 'id_card' ? (
                            <ActionButton tone="info" onClick={openIdCard}>
                              View ID card
                            </ActionButton>
                          ) : null)
                        )
                      }
                    />
                  )
                })}
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Activity
              </Typography>
              {current.activity.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nothing yet.
                </Typography>
              ) : (
                current.activity.map((a, i) => (
                  <Box
                    key={`${a.at}-${i}`}
                    display="flex"
                    py={0.5}
                    style={{ gap: 12 }}
                  >
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ width: 110, flexShrink: 0 }}
                    >
                      {dayjs(a.at).format('D MMM, h:mm A')}
                    </Typography>
                    <Typography variant="body2">{a.text}</Typography>
                  </Box>
                ))
              )}
            </DialogContent>
            <DialogActions>
              <ActionButton tone="neutral" onClick={() => setSelected(null)}>
                Close
              </ActionButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={Boolean(confirmRemove)}
        onClose={() => !removing && setConfirmRemove(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ color: TONES.danger }}>
          Remove {confirmRemove?.name}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            {confirmRemove?.name} will disappear from New Joiners and Bella will
            stop everything for them: no more emails, reminders or steps.
          </Typography>
          <Typography variant="body2">
            You can&apos;t undo this from Alfred. If they are just delayed or
            have a question, use Edit instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <ActionButton
            tone="neutral"
            autoFocus
            disabled={removing}
            onClick={() => setConfirmRemove(null)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            tone="danger"
            filled
            disabled={removing}
            onClick={removeCurrent}
          >
            {removing ? 'Removing…' : 'Yes, remove'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      <DetailsDialog joiner={detailsFor} onClose={() => setDetailsFor(null)} />

      <PreviewDialog
        preview={preview}
        onClose={() => setPreview(null)}
        onOpenPdf={openOfferPdf}
      />

      <JoinerDialog
        open={Boolean(dialog)}
        initial={dialogInitial}
        roles={data.roles}
        centres={data.centres}
        employmentTypes={data.employment_types || []}
        testMode={testMode}
        testTo={testTo}
        onClose={() => setDialog(null)}
        onSave={save}
      />
    </Box>
  )
}

export default NewJoiners
